import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { ReactiveFormsModule, Validators } from '@angular/forms'
import { delayUnitSelectOptions } from '@employer/app/components/jobs-automation/job-automation.model'
import { AutomationHeaderInfoComponent } from '@employer/app/components/jobs-automation/partials/automation-header-info.component'
import {
  E11DynamicFormBuilderComponent,
  E11DynamicFormModule,
  e11FieldInputNumber,
  e11FieldInputSelect,
  e11FieldInputSwitch,
  e11FormFor,
  E11FormForType,
  e11FormLayoutFullWidth,
  e11FormLayoutInlineGrid,
  e11LayoutGroup,
} from '@engineering11/ui-lib/e11-form-builder'
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label'
import { E11SwitchModule } from '@engineering11/ui-lib/e11-switch'
import { TranslateModule } from '@ngx-translate/core'

import { AutomationPopupHelperModalComponent } from '../partials/automation-popup-helper-modal.component'
import { ALL_YOU_NEED, HEADER_INFO, WHAT_HAPPENS } from './automated-rejection.constants'
import { IRejectCandidateFormData, rejectCandidateDefaults, rejectionReasonSelectOptions } from './candidate-rejection.model'

type RejectCandidate = E11FormForType<IRejectCandidateFormData>

@Component({
  selector: 'job-automation-candidate-rejection-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    E11DynamicFormModule,
    E11InputLabelModule,
    E11SwitchModule,
    AutomationHeaderInfoComponent,
    AutomationPopupHelperModalComponent,
  ],
  template: `
    <div class="e11-flex e11-gap-2 e11-mb-2 e11-items-center">
      <h3 class="e11-text-skin-base">{{ 'Candidate Rejection ' | translate }}</h3>

      <e11-switch
        [checked]="rejectCandidateForm.form.get('enabled')?.value ?? false"
        (changes)="rejectCandidateForm.form?.get('enabled')?.setValue($event)"
        [size]="'xs'"
        [color]="'success'"
      ></e11-switch>
    </div>
    <div class="md:e11-flex e11-gap-2">
      <div class="e11-flex-1">
        <p>
          {{
            'The candidate will be rejected therefore no longer considered a candidate for the job. This will send a system notification to the candidate.'
              | translate
          }}
          <automation-popup-helper-modal
            [title]="'Automation Configuration - Reject Candidate'"
            [allYouNeed]="ALL_YOU_NEED"
            [whatHappens]="WHAT_HAPPENS"
            [headerInfo]="HEADER_INFO"
          />
        </p>
      </div>
      <div class="e11-flex-1">
        <e11-dynamic-form-builder
          #rejectCandidateForm
          [loading]="loading"
          [fieldGroups]="form"
          [initialValue]="formData"
          [hideSubmitMenu]="true"
          (valueChanges)="onValueChanges($event)"
        >
          <ng-template e11Template="delayTimeHeaderTemplate">
            <automation-header-info
              label="When are they rejected?"
              toolTipText="This is delayed from the time a request for more information or interview questions were sent to a candidate."
            >
            </automation-header-info>
          </ng-template>
        </e11-dynamic-form-builder>
      </div>
    </div>
  `,
})
export class AutomationCandidateRejectionFormComponent implements OnInit {
  @ViewChild('rejectCandidateForm') rejectCandidateForm!: E11DynamicFormBuilderComponent<IRejectCandidateFormData>
  @Input() loading: boolean = false
  @Input() formData?: IRejectCandidateFormData = rejectCandidateDefaults
  @Output() valueChanges = new EventEmitter<IRejectCandidateFormData>()
  form!: RejectCandidate
  ALL_YOU_NEED = ALL_YOU_NEED
  WHAT_HAPPENS = WHAT_HAPPENS
  HEADER_INFO = HEADER_INFO

  ngOnInit(): void {
    this.form = this.buildRejectCandidateForm()
  }
  onValueChanges(value: IRejectCandidateFormData) {
    this.valueChanges.emit(value)
  }

  private buildRejectCandidateForm(): E11FormForType<IRejectCandidateFormData> {
    const { enabled, delayDuration, delayUnits, rejectionReason } = rejectCandidateDefaults

    return e11FormFor<IRejectCandidateFormData>()([
      e11FormLayoutFullWidth(
        [
          e11FieldInputSwitch('enabled', 'Enabled', enabled, {
            validatorOrOpts: [],
          }),
        ],
        { containerClass: 'e11-hidden' }
      ),
      e11LayoutGroup([
        e11LayoutGroup([
          e11FormLayoutFullWidth([], {
            containerClass: 'e11-mb-0',
            templateSpec: [
              {
                templateKey: 'delayTimeHeaderTemplate',
                templatePosition: 'top',
              },
            ],
          }),
          e11FormLayoutInlineGrid(
            [
              e11FieldInputNumber('delayDuration', 'Time frame:', delayDuration, {
                validatorOrOpts: [Validators.required, Validators.min(0)],
                hideInputError: true,
              }),
              e11FieldInputSelect('delayUnits', 'Unit', delayUnitSelectOptions, delayUnits, {
                validatorOrOpts: [Validators.required],
                hideInputError: true,
                isDisabled: true,
              }),
              e11FieldInputSelect('rejectionReason', 'Rejection Reason:', rejectionReasonSelectOptions, rejectionReason, {
                validatorOrOpts: [Validators.required],
                hideInputError: true,
              }),
            ],
            {
              fieldsClass: 'xl:e11-w-1/4 xl:last:e11-w-1/2',
              containerClass: 'xl:e11-w-full e11-mb-4 e11-hidden xl:e11-flex',
            }
          ),

          e11FormLayoutInlineGrid(
            [
              e11FieldInputNumber('delayDuration', 'Time frame:', delayDuration, {
                validatorOrOpts: [Validators.required, Validators.min(0)],
                hideInputError: true,
              }),
              e11FieldInputSelect('delayUnits', 'Unit', delayUnitSelectOptions, delayUnits, {
                validatorOrOpts: [Validators.required],
                hideInputError: true,
                isDisabled: true,
              }),
            ],
            {
              fieldsClass: 'e11-w-full',
              containerClass: 'xl:e11-w-3/4 e11-mb-4 xl:e11-hidden',
            }
          ),
          e11FormLayoutFullWidth(
            [
              e11FieldInputSelect('rejectionReason', 'Rejection Reason:', rejectionReasonSelectOptions, rejectionReason, {
                validatorOrOpts: [Validators.required],
                hideInputError: true,
              }),
            ],
            {
              containerClass: 'xl:e11-w-3/4 e11-mb-4 xl:e11-hidden',
            }
          ),
        ]),
      ]),
    ])
  }
}
